/**
 * What this device is carrying, what it still owes the server, and what the
 * server refused.
 *
 * The old banner could only say "no connection", which is the least useful
 * true thing to tell someone — it names the weather, not their work. With a
 * queue behind it the honest statement is different: the app is still working,
 * their changes are held, and here is the count.
 *
 * Refusals get their own treatment because they are the only state here a
 * person has to do something about. A change the server accepted and could not
 * land looks exactly like success from a status code (D15), and if this is
 * quiet about it the tech finds out a week later.
 */

import { useOffline } from "../lib/offline";

export function SyncStatus() {
  const { state, syncing, last, online, sync, acknowledgeRefused } = useOffline();

  if (!state) return null;

  const refused = state.refused > 0;
  const waiting = state.pending > 0;

  // Nothing to say: online, nothing queued, nothing refused. Silence is the
  // right output — a status bar that is always visible stops being read.
  if (online && !waiting && !refused) return null;

  return (
    <div
      className={`sync-status ${refused ? "sync-status--refused" : online ? "sync-status--waiting" : "sync-status--offline"}`}
      role="status"
    >
      <div className="sync-status__text">
        {refused ? (
          <>
            <strong>
              {state.refused} change{state.refused > 1 ? "s were" : " was"} refused by the server.
            </strong>{" "}
            They have not been saved. Check the job before you leave site.
          </>
        ) : !online ? (
          <>
            <strong>No connection.</strong>{" "}
            {waiting
              ? `${state.pending} change${state.pending > 1 ? "s" : ""} held here — they'll send when you're back in range.`
              : "Working from what's on this device."}
          </>
        ) : (
          <>
            {state.pending} change{state.pending > 1 ? "s" : ""} still to send.
          </>
        )}
      </div>

      <div className="sync-status__actions">
        {refused ? (
          <button type="button" className="btn btn-sm sync-status__btn" onClick={() => void acknowledgeRefused()}>
            Dismiss
          </button>
        ) : null}
        <button
          type="button"
          className="btn btn-sm sync-status__btn"
          onClick={() => void sync()}
          disabled={syncing}
        >
          {syncing ? "Syncing…" : "Sync now"}
        </button>
      </div>

      {last && !last.offline && last.message !== "Up to date." ? (
        <div className="sync-status__last">{last.message}</div>
      ) : null}
    </div>
  );
}

/**
 * The quieter version, for a settings screen: what is on the device and when
 * it last came down.
 */
export function SyncSummary() {
  const { state, syncing, sync } = useOffline();
  if (!state) return null;

  return (
    <div className="sync-summary">
      <dl>
        <div>
          <dt>Carrying</dt>
          <dd>{state.carrying.toLocaleString()} records</dd>
        </div>
        <div>
          <dt>Waiting to send</dt>
          <dd>{state.pending}</dd>
        </div>
        <div>
          <dt>Last working set</dt>
          <dd>
            {state.hydratedAt
              ? new Date(state.hydratedAt).toLocaleString("en-AU", {
                  weekday: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : "never"}
            {state.stale ? " — out of date" : ""}
          </dd>
        </div>
      </dl>
      <button
        type="button"
        className="btn"
        onClick={() => void sync({ force: true })}
        disabled={syncing || state.pending > 0}
        // Forcing a working set clears the entity stores, so it waits until
        // the queue is empty rather than clearing work off the screen that is
        // still waiting to send.
        title={state.pending > 0 ? "Send the queued changes first" : undefined}
      >
        {syncing ? "Syncing…" : "Get a fresh working set"}
      </button>
    </div>
  );
}
